import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { GET_PARAGRAPHS } from '../../graphql/paragraph-queries';
import {
  CREATE_PARAGRAPH,
  UPDATE_PARAGRAPH,
  DELETE_PARAGRAPH,
} from '../../graphql/paragraph-mutations';
import { ensureParagraphsSeed } from './paragraphsStore';
import { API_URL, getToken } from '../../config';
import AIParagraphLinesSuggester from './AIParagraphLinesSuggester';

/**
 * ParagraphAdmin — GraphQL-backed CRUD for reading passages.
 * Per-sentence audio uploads via the new /api/paragraph-audio/{id}/{idx} route.
 */

const EMPTY_SENTENCE = { zh: '', py: '', en: '', hint: '', audio: '' };

function emptyDraft() {
  return {
    title: '',
    subtitle: '',
    hskLevel: 3,
    timeMinutes: 3,
    sentences: [{ ...EMPTY_SENTENCE }],
    referenceTranslation: '',
  };
}

function Toast({ message, type, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '10px 18px',
        background: type === 'error' ? 'var(--vermillion)' : 'var(--ink)',
        color: '#fff',
        borderRadius: 'var(--r2)',
        fontSize: 14,
        fontWeight: 500,
        zIndex: 10000,
        boxShadow: 'var(--sh3)',
      }}
    >
      {message}
    </div>
  );
}

function PassageCard({ passage, onEdit, onDelete, confirmDeleteId }) {
  const isConfirming = confirmDeleteId === passage.id;
  const audioCount = (passage.sentences || []).filter((s) => s.audio).length;
  return (
    <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 12,
        }}
      >
        <span className="badge badge--mono">HSK {passage.hskLevel}</span>
        {audioCount > 0 && (
          <span className="badge" title={`${audioCount} sentence(s) with recorded audio`}>
            🔊 {audioCount}
          </span>
        )}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-han)',
          fontSize: 32,
          lineHeight: 1.1,
          letterSpacing: '0.03em',
          marginBottom: 4,
          color: 'var(--ink)',
        }}
      >
        {passage.title || '(untitled)'}
      </div>
      <div className="pinyin" style={{ fontSize: 14, marginBottom: 16 }}>
        {passage.subtitle}
      </div>
      <div className="t-small" style={{ marginBottom: 12 }}>
        <span className="t-mono">{passage.sentences?.length || 0}</span> sentence
        {(passage.sentences?.length || 0) === 1 ? '' : 's'} ·{' '}
        <span className="t-mono">{passage.charCount}</span> characters
      </div>
      <div style={{ marginTop: 'auto', display: 'flex', gap: 8 }}>
        <button
          className="btn btn--ghost btn--sm"
          style={{ flex: 1 }}
          onClick={() => onEdit(passage)}
        >
          Edit
        </button>
        {isConfirming ? (
          <button
            className="btn btn--danger btn--sm"
            style={{ flex: 1 }}
            onClick={() => onDelete(passage.id)}
          >
            Click again to confirm
          </button>
        ) : (
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => onDelete(passage.id)}
            style={{ color: 'var(--vermillion)' }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * SentenceAudio — small inline recorder/uploader for a single sentence.
 * Only enabled once the passage has an id (i.e. has been saved at least once).
 */
function SentenceAudio({ paragraphId, sentenceIndex, audioUrl, onChange }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const resolved = audioUrl
    ? audioUrl.startsWith('http')
      ? audioUrl
      : `${API_URL}${audioUrl}`
    : null;

  const handleUpload = async (file) => {
    if (!paragraphId) {
      setErr('Save the passage first.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('audio', file);
      const headers = {};
      const token = getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(
        `${API_URL}/api/paragraph-audio/${paragraphId}/${sentenceIndex}`,
        { method: 'POST', body: fd, headers }
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      onChange(data.url);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!paragraphId) return;
    setBusy(true);
    setErr(null);
    try {
      const headers = {};
      const token = getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(
        `${API_URL}/api/paragraph-audio/${paragraphId}/${sentenceIndex}`,
        { method: 'DELETE', headers }
      );
      if (!res.ok) throw new Error(await res.text());
      onChange('');
    } catch (e) {
      setErr(e.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = () => {
    if (!resolved) return;
    new Audio(resolved).play().catch(() => {});
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center',
        padding: '8px 0',
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleUpload(f);
        }}
      />
      {resolved ? (
        <>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={handlePreview}
          >
            ▶ Play
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            Replace
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={handleDelete}
            disabled={busy}
            style={{ color: 'var(--vermillion)' }}
          >
            Remove
          </button>
        </>
      ) : (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy || !paragraphId}
          title={!paragraphId ? 'Save the passage first' : 'Upload an audio file'}
        >
          {busy ? 'Uploading…' : '🔊 Upload audio'}
        </button>
      )}
      {!paragraphId && !resolved && (
        <span className="t-small">Save passage to enable audio</span>
      )}
      {err && <span className="t-small" style={{ color: 'var(--error)' }}>{err}</span>}
    </div>
  );
}

function PassageEditor({ initial, onClose, onSave, onSentenceAudioChange }) {
  const [draft, setDraft] = useState(() =>
    initial
      ? {
          title: initial.title || '',
          subtitle: initial.subtitle || '',
          hskLevel: initial.hskLevel || 3,
          timeMinutes: initial.timeMinutes || 3,
          sentences: (initial.sentences || []).map((s) => ({
            zh: s.zh || '',
            py: s.py || '',
            en: s.en || '',
            hint: s.hint || '',
            audio: s.audio || '',
          })),
          referenceTranslation: initial.referenceTranslation || '',
        }
      : emptyDraft()
  );

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setSentence = (idx, k, v) =>
    setDraft((d) => ({
      ...d,
      sentences: d.sentences.map((s, i) => (i === idx ? { ...s, [k]: v } : s)),
    }));
  const addSentence = () =>
    setDraft((d) => ({ ...d, sentences: [...d.sentences, { ...EMPTY_SENTENCE }] }));
  const removeSentence = (idx) =>
    setDraft((d) => ({
      ...d,
      sentences: d.sentences.length > 1 ? d.sentences.filter((_, i) => i !== idx) : d.sentences,
    }));

  // AI line suggestion panel toggle. Replaces the trailing empty sentence (if
  // any) when a candidate is accepted, so the form doesn't bloat with blanks.
  const [showAISuggester, setShowAISuggester] = useState(false);
  const acceptAILine = (line) =>
    setDraft((d) => {
      const cleaned = {
        zh: line.zh || '',
        py: line.py || '',
        en: line.en || '',
        hint: line.hint || '',
        audio: '',
      };
      const sentences = [...d.sentences];
      const lastIsEmpty =
        sentences.length > 0 &&
        !sentences[sentences.length - 1].zh &&
        !sentences[sentences.length - 1].py &&
        !sentences[sentences.length - 1].en;
      if (lastIsEmpty) {
        sentences[sentences.length - 1] = cleaned;
      } else {
        sentences.push(cleaned);
      }
      return { ...d, sentences };
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    const cleaned = {
      ...draft,
      title: draft.title.trim(),
      subtitle: draft.subtitle.trim(),
      referenceTranslation: draft.referenceTranslation.trim(),
      sentences: draft.sentences
        .map((s) => ({
          zh: (s.zh || '').trim(),
          py: (s.py || '').trim(),
          en: (s.en || '').trim(),
          hint: (s.hint || '').trim(),
          audio: s.audio || '',
        }))
        .filter((s) => s.zh),
    };
    if (cleaned.sentences.length === 0) return;
    onSave(cleaned);
  };

  const isExisting = Boolean(initial?.id);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,17,13,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 32,
        zIndex: 9000,
        overflowY: 'auto',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 820,
          background: 'var(--card)',
          border: '1px solid var(--rule)',
          borderRadius: 'var(--r3)',
          boxShadow: 'var(--sh4)',
          fontFamily: 'var(--font-sans)',
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            background: 'var(--card)',
            padding: '24px 32px',
            borderBottom: '1px solid var(--rule)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: 'var(--r3) var(--r3) 0 0',
            zIndex: 1,
          }}
        >
          <div>
            <div className="t-eyebrow" style={{ marginBottom: 4 }}>
              {isExisting ? 'Edit passage' : 'New passage'}
            </div>
            <h2 className="t-h2">{isExisting ? draft.title || 'Untitled' : 'Add a reading'}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r2)',
              background: 'transparent',
              color: 'var(--ink-2)',
              cursor: 'pointer',
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div style={{ padding: 32 }}>
          {/* Basic */}
          <section style={{ marginBottom: 32 }}>
            <h3
              className="t-h3"
              style={{ paddingBottom: 8, borderBottom: '2px solid var(--ink)', marginBottom: 16 }}
            >
              <span className="t-mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--ink-3)', marginRight: 8 }}>01</span>
              Basic information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="field">
                <label className="field-label">Title (Chinese) *</label>
                <input
                  className="input"
                  value={draft.title}
                  onChange={(e) => setField('title', e.target.value)}
                  placeholder="例如:我的家乡"
                  style={{ fontFamily: 'var(--font-han)', fontSize: 18 }}
                />
              </div>
              <div className="field">
                <label className="field-label">Subtitle (English)</label>
                <input
                  className="input"
                  value={draft.subtitle}
                  onChange={(e) => setField('subtitle', e.target.value)}
                  placeholder="My hometown"
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label className="field-label">HSK level</label>
                <select
                  className="select"
                  value={draft.hskLevel}
                  onChange={(e) => setField('hskLevel', Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>HSK {n}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="field-label">Time estimate (minutes)</label>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={30}
                  value={draft.timeMinutes}
                  onChange={(e) => setField('timeMinutes', Number(e.target.value))}
                />
              </div>
            </div>
          </section>

          {/* Sentences */}
          <section style={{ marginBottom: 32 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                paddingBottom: 8,
                borderBottom: '2px solid var(--ink)',
                marginBottom: 16,
              }}
            >
              <h3 className="t-h3">
                <span className="t-mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--ink-3)', marginRight: 8 }}>02</span>
                Sentences
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setShowAISuggester((v) => !v)}
                  disabled={!draft.title.trim()}
                  title={!draft.title.trim() ? 'Add a title first' : 'Suggest continuation lines with AI'}
                >
                  ✨ Suggest more lines
                </button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={addSentence}>
                  + Add sentence
                </button>
              </div>
            </div>

            {showAISuggester && (
              <AIParagraphLinesSuggester
                title={draft.title}
                hskLevel={draft.hskLevel}
                existingSentences={draft.sentences.filter((s) => s.zh)}
                count={3}
                onAccept={acceptAILine}
                onClose={() => setShowAISuggester(false)}
              />
            )}

            {draft.sentences.map((s, idx) => (
              <div
                key={idx}
                style={{
                  background: 'var(--paper-2)',
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--r3)',
                  padding: 16,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span className="t-mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
                    Sentence {String(idx + 1).padStart(2, '0')}
                  </span>
                  {draft.sentences.length > 1 && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      style={{ color: 'var(--vermillion)' }}
                      onClick={() => removeSentence(idx)}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label className="field-label">Chinese *</label>
                  <input
                    className="input"
                    value={s.zh}
                    onChange={(e) => setSentence(idx, 'zh', e.target.value)}
                    style={{ fontFamily: 'var(--font-han)' }}
                  />
                </div>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label className="field-label">Pinyin</label>
                  <input
                    className="input"
                    value={s.py}
                    onChange={(e) => setSentence(idx, 'py', e.target.value)}
                  />
                </div>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label className="field-label">English gloss</label>
                  <input
                    className="input"
                    value={s.en}
                    onChange={(e) => setSentence(idx, 'en', e.target.value)}
                  />
                </div>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label className="field-label">Vocabulary hint (optional)</label>
                  <input
                    className="input"
                    value={s.hint}
                    onChange={(e) => setSentence(idx, 'hint', e.target.value)}
                    placeholder="word = meaning; word = meaning"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Audio</label>
                  <SentenceAudio
                    paragraphId={initial?.id}
                    sentenceIndex={idx}
                    audioUrl={s.audio}
                    onChange={(url) => {
                      setSentence(idx, 'audio', url);
                      onSentenceAudioChange && onSentenceAudioChange();
                    }}
                  />
                </div>
              </div>
            ))}
          </section>

          {/* Reference translation */}
          <section style={{ marginBottom: 32 }}>
            <h3
              className="t-h3"
              style={{ paddingBottom: 8, borderBottom: '2px solid var(--ink)', marginBottom: 16 }}
            >
              <span className="t-mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--ink-3)', marginRight: 8 }}>03</span>
              Reference translation
            </h3>
            <p className="t-small" style={{ marginBottom: 12 }}>
              The polished English translation students will see after submitting their attempt.
            </p>
            <textarea
              className="textarea"
              value={draft.referenceTranslation}
              onChange={(e) => setField('referenceTranslation', e.target.value)}
              rows={4}
              placeholder="The polished English translation..."
              style={{ minHeight: 100 }}
            />
          </section>
        </div>

        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: 'var(--card)',
            padding: '16px 32px',
            borderTop: '1px solid var(--rule)',
            display: 'flex',
            gap: 12,
            justifyContent: 'flex-end',
            borderRadius: '0 0 var(--r3) var(--r3)',
          }}
        >
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--accent" disabled={!draft.title.trim()}>
            Save passage
          </button>
        </div>
      </form>
    </div>
  );
}

function ParagraphAdmin({ onBack }) {
  const client = useApolloClient();
  const { data, loading, refetch } = useQuery(GET_PARAGRAPHS, {
    fetchPolicy: 'cache-and-network',
  });
  const [createParagraph] = useMutation(CREATE_PARAGRAPH);
  const [updateParagraph] = useMutation(UPDATE_PARAGRAPH);
  const [deleteParagraph] = useMutation(DELETE_PARAGRAPH);

  const passages = data?.paragraphs || [];
  const [editing, setEditing] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [toast, setToast] = useState(null);
  const seededRef = useRef(false);

  // One-shot migration / seed for first visit.
  useEffect(() => {
    if (loading || seededRef.current) return;
    if (passages.length === 0) {
      seededRef.current = true;
      ensureParagraphsSeed(client, passages).then(() => refetch());
    }
  }, [loading, passages, client, refetch]);

  const handleSave = useCallback(
    async (data) => {
      try {
        if (editing && editing.id) {
          const { data: result } = await updateParagraph({
            variables: { id: editing.id, input: data },
          });
          setToast({ message: 'Passage updated', type: 'success' });
          // Keep the editor open with fresh data so audio uploads find an id.
          setEditing(result?.updateParagraph || null);
        } else {
          const { data: result } = await createParagraph({ variables: { input: data } });
          setToast({ message: 'Passage created', type: 'success' });
          setEditing(result?.createParagraph || null);
        }
        await refetch();
      } catch (err) {
        setToast({ message: err.message || 'Save failed', type: 'error' });
      }
    },
    [editing, createParagraph, updateParagraph, refetch]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (confirmDeleteId === id) {
        try {
          await deleteParagraph({ variables: { id } });
          await refetch();
          setConfirmDeleteId(null);
          setToast({ message: 'Passage deleted', type: 'success' });
        } catch (err) {
          setToast({ message: err.message || 'Delete failed', type: 'error' });
        }
      } else {
        setConfirmDeleteId(id);
        setTimeout(() => setConfirmDeleteId((c) => (c === id ? null : c)), 3000);
      }
    },
    [confirmDeleteId, deleteParagraph, refetch]
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      {toast && (
        <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />
      )}

      <header className="ink-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button className="btn btn--ghost btn--sm" onClick={onBack}>
            ← Back
          </button>
          <span style={{ fontWeight: 500 }}>Paragraph library</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--accent" onClick={() => setEditing({})}>
            + Add passage
          </button>
        </div>
      </header>

      <div style={{ padding: '32px 40px' }}>
        <div style={{ display: 'flex', gap: 32, marginBottom: 24, alignItems: 'baseline' }}>
          <div>
            <div className="t-eyebrow">Total passages</div>
            <div style={{ fontSize: 32, fontWeight: 500, marginTop: 4 }}>{passages.length}</div>
          </div>
          <div style={{ width: 1, height: 32, background: 'var(--rule)' }} />
          <div>
            <div className="t-eyebrow">Storage</div>
            <div style={{ fontSize: 14, marginTop: 8, color: 'var(--ink-3)' }}>
              <span className="t-mono">MongoDB</span> · scoped to your account
            </div>
          </div>
        </div>

        {loading && passages.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <p className="t-body">Loading passages…</p>
          </div>
        ) : passages.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <p className="t-body" style={{ marginBottom: 16 }}>No passages yet.</p>
            <button className="btn btn--accent" onClick={() => setEditing({})}>
              + Add your first passage
            </button>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
            }}
          >
            {passages.map((p) => (
              <PassageCard
                key={p.id}
                passage={p}
                onEdit={(passage) => setEditing(passage)}
                onDelete={handleDelete}
                confirmDeleteId={confirmDeleteId}
              />
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <PassageEditor
          initial={editing && editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          onSentenceAudioChange={() => refetch()}
        />
      )}
    </div>
  );
}

export default ParagraphAdmin;
